import React, { useEffect, useRef, useState } from "react"

import { gql, useQuery } from "hooks/urql"

import LocalTime from "components/LocalTime"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

type TranscriptViewerProps = {
  sessionId: string
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ sessionId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const viewerRef = useRef<HTMLDivElement>(null)

  const [result] = useQuery({
    query: transcriptChunksQuery,
    variables: { sessionId },
    pause: !isOpen,
  })

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const isOutside =
        viewerRef.current && !viewerRef.current.contains(event.target as Node)
      if (isOutside) setIsOpen(false)
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false)
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen])

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-info"
        onClick={() => setIsOpen(!isOpen)}
      >
        View Transcript
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-25"
          onClick={() => setIsOpen(false)}
        >
          <div
            ref={viewerRef}
            className="max-h-[80vh] w-[600px] overflow-y-auto rounded-lg bg-white shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <UrqlLoadingIndicator result={result}>
              {({ data: { airtutorsSession } }) => (
                <div className="space-y-1 p-4 font-mono text-sm">
                  <p className="mb-3">
                    <LocalTime
                      timestamp={airtutorsSession.transcriptChunks[0].spokenAt}
                    />
                  </p>
                  {airtutorsSession.transcriptChunks.map(chunk => (
                    <div key={chunk.id}>
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 whitespace-nowrap text-xs text-gray-400">
                          {chunk.formattedTimestamp}
                        </span>
                        <div className="break-words text-gray-600">
                          <span className="font-semibold">
                            {chunk.speakerName}
                          </span>
                          <p>{chunk.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  <p>End of transcript</p>
                </div>
              )}
            </UrqlLoadingIndicator>
          </div>
        </div>
      )}
    </>
  )
}

const transcriptChunksQuery = gql`
  query TranscriptChunks($sessionId: ID!) {
    airtutorsSession(id: $sessionId) {
      id
      transcriptChunks {
        id
        text
        spokenAt
        formattedTimestamp
        speakerName
      }
    }
  }
`

export default TranscriptViewer
